import { useState } from 'react';
import { Modal } from '@adiox/ui';
import { useActiveLogin } from '../trading/useTrading';
import { markGuideSeen } from './guide';
import { useLayout } from './layout';
import { useAccountLabels } from './useAccountLabels';

type Step = { title: string; text: string };

const DESKTOP: Step[] = [
  {
    title: 'Prices',
    text: 'Market Watch, on the left, shows live prices. Click a symbol to show it on the chart and in the order ticket, double-click it to trade, and right-click for its specification and more. "+ Add" follows more symbols.',
  },
  {
    title: 'The chart',
    text: 'Pick a timeframe with the buttons or the keys 1 to 9. The toolbar has drawing tools, indicators and templates; what does not fit is under ⋯. Your trades show on the chart as arrows, and you can drag stop-loss and take-profit lines to move them.',
  },
  {
    title: 'Placing a trade',
    text: 'New order (or F9) opens the order ticket. Choose the volume, add a stop loss and take profit (the ticket shows what each would make or lose), then Buy or Sell. One-click trading on the chart stays off until you turn it on.',
  },
  {
    title: 'Your account and trades',
    text: 'The top bar shows your balance, equity and margin level; hover a figure for what it means. The Trade panel lists open positions and orders: double-click one to modify it, right-click for more.',
  },
  {
    title: 'Making it yours',
    text: 'View arranges the panels, and Settings (in the menu with your account number) has the theme, sounds, time zone and number format. Press ? at any time for the keyboard shortcuts.',
  },
];

const MOBILE: Step[] = [
  {
    title: 'Prices',
    text: 'Quotes shows live prices. Tap a symbol for New order, Open chart, its specification or Hide.',
  },
  {
    title: 'The chart',
    text: 'The Chart tab shows the symbol you picked. Change the timeframe at the top; drawing tools and indicators are in the toolbar and under ⋯.',
  },
  {
    title: 'Placing a trade',
    text: 'New order, at the top right, opens the order ticket. Choose the volume, add a stop loss (you see what it risks), then Buy or Sell.',
  },
  {
    title: 'Your account and trades',
    text: 'Your equity and profit are at the top: tap them for every figure. The Trade tab lists your positions with Modify and Close buttons.',
  },
  {
    title: 'More',
    text: 'More has notifications, price alerts and Settings. Add the terminal to your home screen to open it like an app.',
  },
];

/**
 * The first-run guide: a short tour of where things are, in five steps. It opens once for a new trader (skippable at
 * every step) and can be opened again from the menu.
 */
export function WelcomeGuide({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { mobile } = useLayout();
  const steps = mobile ? MOBILE : DESKTOP;
  const [at, setAt] = useState(-1); // -1: the welcome
  const login = useActiveLogin();
  const labels = useAccountLabels();
  const label = login === undefined ? undefined : labels.get(login);
  const finish = () => {
    markGuideSeen();
    setAt(-1);
    onClose();
  };
  const step = at >= 0 ? steps[at]! : null;
  const button = 'rounded-md border border-line px-3 py-1.5 text-sm hover:border-accent';

  return (
    <Modal open={open} title={step ? step.title : 'Welcome to Adiox'} onClose={finish} size="md">
      <div className="flex flex-col gap-4" data-testid="welcome-guide">
        {step ? (
          <p className="text-sm leading-relaxed">{step.text}</p>
        ) : (
          <div className="flex flex-col gap-2 text-sm leading-relaxed">
            <p>
              This is your trading terminal{login !== undefined ? `, signed in with account #${login}` : ''}.
              Here is a quick look at where things are: five short steps.
            </p>
            {label?.kind === 'Demo' && (
              <p className="rounded-md border border-line bg-panel2 p-2">
                This is a <strong>demo account</strong>: the money is virtual, so you can practise freely.
              </p>
            )}
            {(label?.kind === 'Challenge' || label?.kind === 'Funded') && (
              <p className="rounded-md border border-line bg-panel2 p-2">
                This is a <strong>prop-firm account</strong> ({label.label}): the loss you may still take
                today and in all is always shown next to your balance.
              </p>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          {step && (
            <span className="text-xs text-muted" aria-live="polite">
              Step {at + 1} of {steps.length}
            </span>
          )}
          <span className="flex-1" />
          <button
            type="button"
            className="text-sm text-muted underline-offset-2 hover:underline"
            onClick={finish}
          >
            {step ? 'Skip the rest' : 'Skip the guide'}
          </button>
          {at >= 0 && (
            <button type="button" className={button} onClick={() => setAt(at - 1)}>
              Back
            </button>
          )}
          {at < steps.length - 1 ? (
            <button
              type="button"
              autoFocus
              className="rounded-md bg-accent px-4 py-1.5 text-sm font-semibold text-accent-fg"
              onClick={() => setAt(at + 1)}
            >
              {at === -1 ? 'Show me' : 'Next'}
            </button>
          ) : (
            <button
              type="button"
              autoFocus
              className="rounded-md bg-accent px-4 py-1.5 text-sm font-semibold text-accent-fg"
              onClick={finish}
            >
              Start trading
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
